// app/features/industry-workflows/components/IndustryWorkflowPublicListComponent.tsx
'use client';

import { 
  AlertTriangle, 
  Inbox, 
  Search,
  Sparkles
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { HTFContentCard } from '@/app/shared/components/cards/HTFContentCard';
import { HTFSectionHeader } from '@/app/shared/components/layout/HTFSectionHeader';
import { LoadingIndicator } from '@/components/ui/loading-indicator';

import type { IndustryWorkflowListItem } from '../industry-workflow.interface';
import { IndustryWorkflowCardComponent } from './IndustryWorkflowCardComponent';

interface IndustryWorkflowPublicListComponentProps {
  workflows: IndustryWorkflowListItem[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export function IndustryWorkflowPublicListComponent({
  workflows,
  totalCount,
  isLoading,
  isError,
  error,
}: IndustryWorkflowPublicListComponentProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');

  // Get unique industries for filter
  const industries = useMemo(() => {
    if (!workflows) return [];
    const uniqueIndustries = new Set(
      workflows
        .map(w => w.input_industry_name)
        .filter((name): name is string => !!name)
    );
    return Array.from(uniqueIndustries).sort();
  }, [workflows]);

  // Filter and search logic
  const filteredWorkflows = useMemo(() => {
    if (!workflows) return [];

    let filtered = workflows;

    // Apply industry filter
    if (selectedIndustry !== 'all') {
      filtered = filtered.filter(w => w.input_industry_name === selectedIndustry);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(workflow => 
        workflow.title?.toLowerCase().includes(query) ||
        workflow.input_industry_name?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [workflows, searchQuery, selectedIndustry]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-16">
          <LoadingIndicator text="Loading AI workflow examples..." />
        </div>
      );
    }

    if (isError) {
      return (
        <HTFContentCard variant='error' padded bordered className="text-center">
          <div className="flex flex-col items-center gap-2">
            <AlertTriangle className="w-8 h-8 text-error" />
            <p className="font-semibold">Error Loading Workflows</p>
            <p className="text-sm">{error?.message || 'An unexpected error occurred.'}</p>
          </div>
        </HTFContentCard>
      );
    }

    if (!workflows || workflows.length === 0) {
      return (
        <HTFContentCard variant='muted' padded bordered className="text-center">
          <div className="flex flex-col items-center gap-3 py-8">
            <Inbox className="w-12 h-12 text-gray-400" />
            <p className="font-semibold text-gray-700">No Workflow Examples Yet</p>
            <p className="text-sm text-gray-500 max-w-sm">
              Check back soon for industry-specific AI workflow examples.
            </p>
          </div>
        </HTFContentCard>
      );
    }

    if (filteredWorkflows.length === 0) {
      return (
        <HTFContentCard variant='muted' padded bordered className="text-center">
          <div className="flex flex-col items-center gap-3 py-8">
            <Search className="w-12 h-12 text-gray-400" />
            <p className="font-semibold text-gray-700">No Results Found</p>
            <p className="text-sm text-gray-500 max-w-sm">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        </HTFContentCard>
      );
    }

    return (
      <div className="space-y-6">
        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {filteredWorkflows.length} of {totalCount} workflow{totalCount !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Grid of workflow cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkflows.map((workflow) => (
            <IndustryWorkflowCardComponent key={workflow.id} workflow={workflow} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <HTFSectionHeader
        title="AI Workflow Examples"
        subtitle="Explore real-world AI workflow blueprints across different industries"
        icon={Sparkles}
      />

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search workflows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Industry Filter */}
        {industries.length > 0 && (
          <select
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            <option value="all">All Industries</option>
            {industries.map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
}

